import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) return;
    setSubmitting(true);
    setError(false);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      await fetch("https://readdy.ai/api/form/d75gfmkprit363gl15mg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  const info = [
    {
      icon: "ri-map-pin-2-line",
      label: "Address",
      lines: ["2645 Broad St", "Bethel Park, PA 15102"],
    },
    {
      icon: "ri-phone-line",
      label: "Phone",
      lines: ["(866) 605-4975"],
      href: "tel:8666054975",
    },
    {
      icon: "ri-time-line",
      label: "Support Hours",
      lines: ["Mon – Fri, 9am – 5pm EST"],
    },
    {
      icon: "ri-mail-line",
      label: "Email",
      lines: ["support@ironhavensupply.com"],
      href: "mailto:support@ironhavensupply.com",
    },
    {
      icon: "ri-government-line",
      label: "Business Registration (EIN)",
      lines: ["EIN: 41-3937806", "Furman Loomis LLC"],
    },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ironhavensupply.com/" },
      { "@type": "ListItem", position: 2, name: "Contact", item: "https://ironhavensupply.com/contact" },
    ],
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "@id": "https://ironhavensupply.com/contact#webpage",
    name: "Contact Iron Haven Supply",
    url: "https://ironhavensupply.com/contact",
    description: "Contact Iron Haven Supply for questions about backup power systems, orders, returns, or warranty support. Reach us by phone, email, or contact form.",
    breadcrumb: { "@id": "https://ironhavensupply.com/contact#breadcrumb" },
    about: {
      "@type": "LocalBusiness",
      "@id": "https://ironhavensupply.com/#business",
      name: "Iron Haven Supply",
      legalName: "Furman Loomis LLC",
      taxID: "41-3937806",
      telephone: "+18666054975",
      email: "support@ironhavensupply.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2645 Broad St",
        addressLocality: "Bethel Park",
        addressRegion: "PA",
        postalCode: "15102",
        addressCountry: "US",
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(contactPageSchema) }}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-14 bg-gray-950 text-white text-center px-4">
        <p className="text-amber-500 text-xs font-bold uppercase tracking-widest mb-3">Get In Touch</p>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mb-3">Contact Iron Haven Supply</h1>
        <p className="text-gray-400 text-sm md:text-base max-w-xl mx-auto">
          Questions about a product, your order, or just need help finding the right backup power system? We&apos;re here.
        </p>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Contact" }]} />
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 py-14 grid grid-cols-1 lg:grid-cols-5 gap-12">

        {/* Left — info + map */}
        <div className="lg:col-span-2 flex flex-col gap-8">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Our Information</h2>
            <div className="flex flex-col gap-5">
              {info.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-amber-50 flex-shrink-0">
                    <i className={`${item.icon} text-amber-500 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="text-gray-800 text-sm font-medium hover:text-amber-600 transition-colors cursor-pointer">
                        {item.lines.join(" ")}
                      </a>
                    ) : (
                      item.lines.map((line, i) => (
                        <p key={i} className="text-gray-800 text-sm font-medium leading-snug">{line}</p>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Map */}
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 mb-4">Find Us</h2>
            <div className="w-full h-64 rounded-lg overflow-hidden border border-gray-100">
              <iframe
                title="Iron Haven Supply location"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU3Kno&q=2645+Broad+St,+Bethel+Park,+PA+15102"
              />
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="lg:col-span-3">
          <h2 className="text-lg font-extrabold text-gray-900 mb-6">Send Us a Message</h2>

          {submitted ? (
            <div className="flex flex-col items-center justify-center text-center gap-4 py-16 border border-emerald-100 rounded-lg bg-emerald-50">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-100">
                <i className="ri-check-line text-2xl text-emerald-600"></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-base">Message sent!</p>
                <p className="text-gray-500 text-sm mt-1">We&apos;ll get back to you within 1 business day.</p>
              </div>
              <button
                onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); }}
                className="text-sm text-amber-600 font-semibold hover:underline cursor-pointer"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form data-readdy-form id="contact-us-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Full Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="John Smith"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                  required
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-amber-400 transition-colors cursor-pointer bg-white"
                >
                  <option value="">Select a topic…</option>
                  <option value="Product Question">Product Question</option>
                  <option value="Order Status">Order Status</option>
                  <option value="Returns & Exchanges">Returns &amp; Exchanges</option>
                  <option value="Shipping Issue">Shipping Issue</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Warranty Claim">Warranty Claim</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                  Message
                  <span className={`ml-2 font-normal normal-case ${form.message.length > 500 ? "text-red-500" : "text-gray-400"}`}>
                    ({form.message.length}/500)
                  </span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  maxLength={500}
                  placeholder="Tell us how we can help…"
                  className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                />
                {form.message.length > 500 && (
                  <p className="text-red-500 text-xs mt-1">Please keep your message under 500 characters.</p>
                )}
              </div>

              {error && (
                <p className="text-red-500 text-sm">Something went wrong — please try again.</p>
              )}

              <button
                type="submit"
                disabled={submitting || form.message.length > 500}
                className="self-start bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white font-semibold text-sm px-8 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
              >
                {submitting ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </main>

      {/* Legitimacy statement */}
      <section className="bg-amber-50 border-t border-amber-100 py-6">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <p className="text-gray-600 text-sm text-center leading-relaxed">
            <strong className="text-gray-800">Iron Haven Supply</strong> operates as a registered U.S. business under <strong className="text-gray-800">Furman Loomis LLC</strong> (EIN: 41-3937806). Our primary business address is located in <strong className="text-gray-800">Bethel Park, Pennsylvania</strong>.
          </p>
        </div>
      </section>

      {/* Quick links bar */}
      <section className="bg-gray-50 border-t border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-gray-500">
          <span>Looking for something specific?</span>
          <div className="flex flex-wrap items-center gap-3 justify-center">
            <Link to="/return-policy" className="text-amber-600 hover:underline font-medium whitespace-nowrap cursor-pointer">Return Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/shipping-policy" className="text-amber-600 hover:underline font-medium whitespace-nowrap cursor-pointer">Shipping Policy</Link>
            <span className="text-gray-300">|</span>
            <Link to="/shop" className="text-amber-600 hover:underline font-medium whitespace-nowrap cursor-pointer">Shop All Products</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
